/**
 * Photo Type Service
 *
 * CRUD operations for the per-company photo taxonomy. Photo Type is a
 * required field on every photo upload; Subcategory is an optional
 * finer-grained child of a Type and is filtered by parent Type in the
 * field UX datalist.
 *
 * Two access tiers (server-enforced):
 *   - Read + create-on-the-fly: any authenticated user. POST /photo-types
 *     is reachable inline so a field user typing a value not in the
 *     datalist auto-creates a new entry. The server forces the
 *     company_id to the user's own company unless they're super_admin.
 *   - Update + delete (and creating in another company as super_admin):
 *     super_admin only.
 *
 * Backend endpoints (under /api/v1):
 *   GET    /photo-types                                — list (auth, scoped)
 *   POST   /photo-types                                — create
 *   GET    /photo-types/{id}                           — show
 *   PATCH  /photo-types/{id}                           — update (super_admin)
 *   DELETE /photo-types/{id}                           — delete (super_admin)
 *   POST   /photo-types/{photoTypeId}/subcategories    — create subcategory
 *   PATCH  /photo-subcategories/{id}                   — update (super_admin)
 *   DELETE /photo-subcategories/{id}                   — delete (super_admin)
 */

import { api } from './api';

/* ============================================
 * TYPES
 * ============================================ */

/**
 * A subcategory nested under a Photo Type.
 *
 * @property isSystem  True for seeded defaults — protected from deletion.
 */
export interface PhotoSubcategory {
    id: string;
    photoTypeId: string;
    name: string;
    isSystem: boolean;
    createdBy: string | null;
    createdAt: string;
    updatedAt: string;
}

/**
 * A top-level photo type scoped to a company. Subcategories are
 * eager-loaded by the index/show endpoints.
 */
export interface PhotoType {
    id: string;
    companyId: string;
    name: string;
    isSystem: boolean;
    createdBy: string | null;
    subcategories: PhotoSubcategory[];
    createdAt: string;
    updatedAt: string;
}

export interface CreatePhotoTypeData {
    name: string;
    /** Required when called by super_admin to create on behalf of a company */
    companyId?: number;
}

export interface UpdatePhotoTypeData {
    name?: string;
    /** Super-admin only — moves a type to a different company */
    companyId?: number;
}

export interface CreatePhotoSubcategoryData {
    name: string;
}

export interface UpdatePhotoSubcategoryData {
    name?: string;
    photoTypeId?: number;
}

/* ============================================
 * SERVICE
 * ============================================ */

export const photoTypeService = {
    /**
     * GET /photo-types — list types accessible to current user.
     * Optional company_id filter (must be in user's accessible set
     * unless super_admin).
     */
    list: (params?: Record<string, string>) =>
        api.get<{ data: PhotoType[] }>('/photo-types', params),

    /** GET /photo-types/{id} — single type with its subcategories */
    getById: (id: string) =>
        api.get<{ data: PhotoType }>(`/photo-types/${id}`),

    /**
     * POST /photo-types — create a new type. Idempotent on
     * (company_id, name) — returns the existing row if present
     * instead of failing the unique constraint.
     */
    create: (data: CreatePhotoTypeData) =>
        api.post<{ data: PhotoType }>('/photo-types', data),

    /** PATCH /photo-types/{id} — super-admin only */
    update: (id: string, data: UpdatePhotoTypeData) =>
        api.patch<{ data: PhotoType }>(`/photo-types/${id}`, data),

    /**
     * DELETE /photo-types/{id} — super-admin only.
     * Server refuses to delete is_system=true defaults.
     */
    delete: (id: string) =>
        api.delete<void>(`/photo-types/${id}`),

    /**
     * POST /photo-types/{photoTypeId}/subcategories — create a subcategory.
     * Open to any auth user (datalist create-on-the-fly).
     */
    createSubcategory: (photoTypeId: string, data: CreatePhotoSubcategoryData) =>
        api.post<{ data: PhotoSubcategory }>(`/photo-types/${photoTypeId}/subcategories`, data),

    /** PATCH /photo-subcategories/{id} — super-admin only */
    updateSubcategory: (id: string, data: UpdatePhotoSubcategoryData) =>
        api.patch<{ data: PhotoSubcategory }>(`/photo-subcategories/${id}`, data),

    /** DELETE /photo-subcategories/{id} — super-admin only */
    deleteSubcategory: (id: string) =>
        api.delete<void>(`/photo-subcategories/${id}`),

    /**
     * Resolve a (companyId, name) pair to a photo_type_id by listing
     * the company's photo types and finding the matching name. Returns
     * null when no match is found.
     *
     * Used by the legacy photo-upload pages to map their hardcoded
     * 'before' / 'after' / 'observation' / 'completed_qc' tags onto
     * the seeded system entries on the per-company taxonomy.
     *
     * Successful resolutions are cached per (companyId, name) for the
     * lifetime of the page so repeated photo uploads don't re-fetch.
     *
     * Failures are deliberately NOT cached. A request that fails because
     * the device is offline — the common case in the field, and exactly
     * when photos are being captured — would otherwise poison the cache
     * with a permanent null, so every upload after the tech regained
     * signal would keep failing with "no photo type configured" until a
     * full page reload. A miss costs one extra request; a cached miss
     * costs the user their photos.
     */
    resolveByName: (() => {
        const cache = new Map<string, number>();
        return async (companyId: string, name: string): Promise<number | null> => {
            const key = `${companyId}::${name.toLowerCase()}`;
            const cached = cache.get(key);
            if (cached !== undefined) return cached;

            try {
                const res = await api.get<{ data: PhotoType[] }>(
                    '/photo-types',
                    { company_id: companyId }
                );
                const rows: PhotoType[] = ((res.data as any)?.data ?? []) as PhotoType[];
                const match = rows.find((t) => t.name.toLowerCase() === name.toLowerCase());
                if (!match) return null;

                const id = Number(match.id);
                cache.set(key, id);
                return id;
            } catch {
                return null;
            }
        };
    })(),
};
